import { Head, Link } from '@inertiajs/react';

/**
 * Guide data for the index.
 */
interface Guide {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    icon: string;
    is_published: boolean;
}

/**
 * Props for the University Index.
 */
interface Props {
    guides: Guide[];
    isGm: boolean;
}

/**
 * University Index - Landing page showing all game guides.
 * Styled as a study hall with parchment-style guide cards.
 */
export default function Index({ guides, isGm }: Props) {
    return (
        <>
            <Head title="University - How to Play" />

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                {/* Header */}
                <header className="bg-gradient-to-r from-blue-900/50 to-indigo-900/50 border-b border-blue-700/30">
                    <div className="max-w-6xl mx-auto px-6 py-8">
                        <div className="flex items-center gap-4">
                            <span className="text-5xl">🎓</span>
                            <div>
                                <h1 className="text-3xl font-serif text-blue-100">
                                    Sellune University
                                </h1>
                                <p className="text-blue-200/70 mt-1">
                                    Learn how to play and master the world of Sellune
                                </p>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Guide Cards */}
                <main className="max-w-6xl mx-auto px-6 py-8">
                    {guides.length === 0 ? (
                        <div className="text-center py-16">
                            <p className="text-blue-200/60 text-lg italic">
                                No guides have been written yet. Check back soon!
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {guides.map((guide) => (
                                <Link
                                    key={guide.id}
                                    href={`/university/${guide.slug}`}
                                    className="group block relative"
                                >
                                    {/* Parchment-style card */}
                                    <div
                                        className="relative rounded-lg p-6 transition-all duration-300 group-hover:scale-[1.02] group-hover:-translate-y-1"
                                        style={{
                                            background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                                            boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                                            border: '1px solid #c9b896',
                                        }}
                                    >
                                        {/* Aged edge effect */}
                                        <div
                                            className="absolute top-0 left-0 right-0 h-2 pointer-events-none rounded-t-lg"
                                            style={{ background: 'linear-gradient(180deg, rgba(139,115,85,0.15) 0%, transparent 100%)' }}
                                        />

                                        {/* Icon and Title */}
                                        <div className="flex items-start gap-4">
                                            <span className="text-4xl flex-shrink-0">{guide.icon}</span>
                                            <div className="flex-1 min-w-0">
                                                <h2 className="text-xl font-serif font-bold text-amber-900 group-hover:text-amber-700 transition-colors">
                                                    {guide.title}
                                                </h2>
                                                {guide.description && (
                                                    <p className="mt-2 text-sm text-amber-800/70 line-clamp-3">
                                                        {guide.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Draft badge for GMs */}
                                        {isGm && !guide.is_published && (
                                            <div className="absolute top-2 right-2">
                                                <span className="px-2 py-0.5 bg-yellow-600 text-white text-xs rounded font-medium">
                                                    Draft
                                                </span>
                                            </div>
                                        )}

                                        {/* Read more indicator */}
                                        <div className="mt-4 flex items-center gap-1 text-amber-700/60 text-sm font-serif">
                                            <span>Read Guide</span>
                                            <span className="group-hover:translate-x-1 transition-transform">→</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* GM Link */}
                    {isGm && (
                        <div className="mt-12 text-center">
                            <Link
                                href="/gm/university"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                <span>⚙️</span>
                                <span>Manage Guides</span>
                            </Link>
                        </div>
                    )}
                </main>
            </div>
        </>
    );
}
